import * as React from "react";

import { Separator } from "@replo/design-system/components/separator/Separator";
import { skipToken } from "@tanstack/react-query";
import InlineAlert from "@v1/components/designSystem/alert/InlineAlert";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { DeprecatedTextarea } from "@v1/components/designSystem/textarea/Textarea";
import { DeprecatedToggleGroup } from "@v1/components/designSystem/toggle/ToggleGroup";
import useUrlHosts from "@v2/features/analytics/hooks/useUrlHosts";
import { analyticsRouter } from "@v2/trpc/routes/analytics";
import { X } from "lucide-react";

import { useAnalyticsQueryContext } from "../contexts/AnalyticsQueryContext";

const useAnalyticsMicroVac = ({
  promptOverride,
}: {
  promptOverride: string;
}) => {
  const { workspaceId, projectId } = useAnalyticsQueryContext();
  const { data: urlHostsObj } = useUrlHosts();

  const shouldFetch =
    (workspaceId || projectId) && (urlHostsObj?.length ?? 0) > 0;

  const { data, isLoading } = analyticsRouter.generateInsights.useQuery(
    shouldFetch
      ? {
          workspaceId: workspaceId ?? "",
          projectId,
          urlHosts: urlHostsObj ?? [],
          promptOverride: promptOverride === "" ? null : promptOverride,
        }
      : skipToken,
  );

  return {
    insights: data?.insights ?? [],
    insightRangeResults: data?.insightRangeResults ?? [],
    isLoading,
  };
};

interface InsightsAIMicrovacModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const InsightsAIMicrovacModal: React.FC<
  InsightsAIMicrovacModalProps
> = ({ isOpen, onClose }) => {
  const [position, setPosition] = React.useState({
    x: window.innerWidth - 1320,
    y: 50,
  });
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragOffset, setDragOffset] = React.useState({ x: 0, y: 0 });

  const modalRef = React.useRef<HTMLDivElement>(null);

  // Handle dragging
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && (e.target as HTMLElement).closest(".cursor-grab")) {
      setIsDragging(true);
      const rect = modalRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y,
        });
      }
    },
    [isDragging, dragOffset],
  );

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={modalRef}
      className="border-primary fixed z-50 flex h-[800px] w-[1200px] flex-col overflow-hidden border-2 bg-white p-4 shadow-lg"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        cursor: isDragging ? "grabbing" : "default",
      }}
    >
      <div
        className="mb-3 flex shrink-0 cursor-grab items-center justify-between pb-2"
        onMouseDown={handleMouseDown}
      >
        <div className="flex flex-1 cursor-grab flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="header-h5 font-mono text-sm uppercase">
              Insights AI Microvac
            </span>
            <DeprecatedIconButton
              icon={<X className="h-4 w-4" />}
              variant="secondary"
              layoutClassName="h-6 w-6"
              onClick={onClose}
            />
          </div>
          <InlineAlert variant="info">
            <span>
              [INTERNAL] Use this to prompt engineer - our current live prod
              prompt is{" "}
              <a
                href="https://www.notion.so/replo/Current-Live-prompt-1bd8170f84a68033afa4ff0d228dcca6?pvs=4"
                target="_blank"
                rel="noreferrer"
                className="text-primary"
              >
                here
              </a>
              : copy it, make changes to it, hit generate to see the insights
              that get generated with that prompt, and iterate!
            </span>
          </InlineAlert>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <InsightsAIMicrovacContent />
      </div>
    </div>
  );
};

type ResultType = "insights" | "rangeResults";

const InsightsAIMicrovacContent: React.FC = () => {
  const [promptOverrideInput, setPromptOverrideInput] =
    React.useState<string>("");

  const [promptOverride, setPromptOverride] = React.useState<string>("");

  const { insights, insightRangeResults, isLoading } = useAnalyticsMicroVac({
    promptOverride,
  });

  const [resultType, setResultType] = React.useState<ResultType>("insights");

  return (
    <div className="flex flex-col gap-3 p-1">
      <div className="flex flex-col gap-2">
        <DeprecatedTextarea
          size="base"
          layoutClassName="h-24 w-full mb-3"
          value={promptOverrideInput}
          onChange={setPromptOverrideInput}
          placeholder="Enter your system prompt override. If empty, our AI will use the system prompt that's currently in prod."
          autoFocus
        />

        <DeprecatedButton
          variant="primary"
          size="base"
          disabled={isLoading}
          onClick={() => {
            setPromptOverride(promptOverrideInput);
          }}
        >
          Generate Insights
        </DeprecatedButton>
      </div>

      <Separator className="my-4" />

      {isLoading ? (
        <span className="font-mono">
          Aight chill, I'm cookin' up them insights. Gimme 1min, these numbers
          ain't gonna hustle themselves...
        </span>
      ) : (
        <div className="flex flex-col gap-4">
          <DeprecatedToggleGroup
            size="sm"
            options={[
              { label: "Insights", value: "insights" },
              {
                label: "ClickHouse Metrics",
                value: "clickhouseMetrics",
              },
            ]}
            selectedValue={resultType}
            onChange={(value) => {
              setResultType(value as ResultType);
            }}
          />
          <pre className="border-0.5 border-border overflow-auto whitespace-pre-wrap rounded-md p-2">
            {JSON.stringify(
              resultType === "insights" ? insights : insightRangeResults,
              null,
              2,
            )}
          </pre>
        </div>
      )}
    </div>
  );
};
